import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { lighten } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTranslation } from 'react-i18next';
import { Container } from '@mui/material';

function getPriorityCellSx(priority) {
    switch (priority) {
        case 2:
            return { display: { xs: 'none', md: 'table-cell' } };
        case 3:
            return { display: { xs: 'none', lg: 'table-cell' } };
        case 4:
            return { display: { xs: 'none', xl: 'table-cell' } };
        case 1:
        default:
            return undefined;
    }
}

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    if (!array) {
        return [];
    }
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

function EnhancedCellHead(props) {
    const { classes, createSortHandler, order, orderBy, headCell, sx } = props;
    return (
        <TableCell
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            // style={{ minWidth: (headCell.minWidth ?? 250) }}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={sx}
        >
            <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                sx={headCell.numeric ? { flexDirection: 'row-reverse' } : undefined}
            >
                {headCell.label}
                {orderBy === headCell.id ? (
                    <span className={classes.visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </span>
                ) : null}
            </TableSortLabel>
        </TableCell>);
}

function EnhancedTableHead(props) {
    const { classes, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, headCells, editable } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {editable === true ? (
                    <TableCell padding="checkbox">
                        <Checkbox
                            indeterminate={numSelected > 0 && numSelected < rowCount}
                            checked={rowCount > 0 && numSelected === rowCount}
                            onChange={onSelectAllClick}
                            inputProps={{ 'aria-label': 'select all desserts' }}
                        />
                    </TableCell>) : (null)}
                {headCells.map((headCell) => (
                    <EnhancedCellHead
                        key={headCell.id}
                        classes={classes}
                        createSortHandler={createSortHandler}
                        order={order}
                        orderBy={orderBy}
                        headCell={headCell}
                        sx={getPriorityCellSx(headCell.priority)}
                    ></EnhancedCellHead>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onAdd: PropTypes.func,
    onDelete: PropTypes.func,
    onEdit: PropTypes.func,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
    },
    highlight:
        theme.palette.mode === 'light'
            ? {
                color: theme.palette.secondary.main,
                backgroundColor: lighten(theme.palette.secondary.light, 0.85),
            }
            : {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.secondary.dark,
            },
    title: {
        flex: '1 1 100%',
    },
}));

const EnhancedTableToolbar = (props) => {
    const classes = useToolbarStyles();
    const { numSelected, title, itemsFilterChanged, editable, onAdd, onDelete, onEdit } = props;
    const { t } = useTranslation("common");

    return (
        <Toolbar
            className={clsx(classes.root, {
                [classes.highlight]: numSelected > 0,
            })}
        >
            {(editable === true && numSelected > 0) ? (
                <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
                    {numSelected} {t("commonLabels.selected")}
                </Typography>
            ) : (
                <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
                    {title}
                </Typography>
            )}

            {onAdd ? (<Tooltip title={t("commonLabels.add").toString()}>
                <IconButton aria-label={t("commonLabels.add")} onClick={onAdd} size="large">
                    <AddIcon />
                </IconButton>
            </Tooltip>) : <></>}

            {(editable === true && numSelected === 1)
                ? (<Tooltip title="Edit">
                    <IconButton aria-label={t("commonLabels.edit")} onClick={onEdit} size="large">
                        <EditIcon />
                    </IconButton>
                </Tooltip>) : (<></>)}
            {(editable === true && numSelected > 0) ?
                (<Tooltip title="Delete">
                    <IconButton aria-label={t("commonLabels.delete")} onClick={onDelete} size="large">
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>)
                : (<React.Fragment>
                    <TextField label={t("commonLabels.filter")} id="itemfilter" onChange={itemsFilterChanged} />
                    <Tooltip title="Filter list">
                        <IconButton aria-label="filter list" size="large">
                            <FilterListIcon />
                        </IconButton>
                    </Tooltip>
                </React.Fragment>
                )
            }
        </Toolbar >
    );
};

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    paper: {
        width: '100%',
        marginBottom: theme.spacing(2),

    },

    table: {
        // minWidth: 750,
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
}));

export default function EnhancedTable(props) {
    const { tableItems, columns, defaultOrderColumn, title, dense, editable, itemClick, onAdd, onEdit, onDelete } = props;

    const classes = useStyles();
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState(defaultOrderColumn);
    const [selected, setSelected] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(-1);
    const [rows, setRows] = React.useState([]);

    const rowCount = tableItems?.length ?? 0;
    const emptyRows = rowsPerPage > 0 ? rowsPerPage - Math.min(rowsPerPage, rowCount - page * rowsPerPage) : 0;
    const rowIds = columns.filter(c => c.rowId === true);
    const [headerColumn, ...otherColumns] = columns.filter(c => c.hidden !== true);
    const rowIdColumn = rowIds.length > 0 ? rowIds[0] : headerColumn;
    const searchableColumns = columns.filter(c => c.searchable === true);

    useEffect(() => {
        setRows(tableItems);
    }, [tableItems]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked && rows) {
            const newSelecteds = rows.map((n) => n.name);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, name) => {
        if (editable === true) {
            const selectedIndex = selected.indexOf(name);
            let newSelected = [];

            if (selectedIndex === -1) {
                newSelected = newSelected.concat(selected, name);
            } else if (selectedIndex === 0) {
                newSelected = newSelected.concat(selected.slice(1));
            } else if (selectedIndex === selected.length - 1) {
                newSelected = newSelected.concat(selected.slice(0, -1));
            } else if (selectedIndex > 0) {
                newSelected = newSelected.concat(
                    selected.slice(0, selectedIndex),
                    selected.slice(selectedIndex + 1),
                );
            }

            setSelected(newSelected);
        } else if (itemClick) {
            itemClick(event, name);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isSelected = (name) => selected.indexOf(name) !== -1;



    const filterItems = (event) => {
        const filterValue = event.target.value.toLowerCase();
        if (filterValue.length > 0 && tableItems) {
            const filteredRows = tableItems.filter(r => {
                for (let colIndex in searchableColumns) {
                    if (r[searchableColumns[colIndex].id]?.toLowerCase()?.indexOf(filterValue) > -1) {
                        return true;
                    }
                }
                return false;
            });
            setRows(filteredRows);
        } else {
            setRows(tableItems);
        }
    }



    return (
        <div className={classes.root}>
            <Paper className={classes.paper}>
                <Container>
                    <EnhancedTableToolbar
                        numSelected={selected.length}
                        title={title}
                        itemsFilterChanged={filterItems}
                        editable={editable}
                        onAdd={onAdd}
                        onEdit={() => {
                            const circuitId = selected[0];
                            onEdit(circuitId);
                            setSelected([]);
                        }}
                        onDelete={() => {
                            onDelete(selected);
                            setSelected([]);
                        }} />
                    <TableContainer>
                        <Table
                            className={classes.table}
                            aria-labelledby="tableTitle"
                            size={dense ? 'small' : 'medium'}
                            aria-label="enhanced table"
                            stickyHeader
                        >
                            <EnhancedTableHead
                                classes={classes}
                                numSelected={selected.length}
                                order={order}
                                orderBy={orderBy}
                                onSelectAllClick={handleSelectAllClick}
                                editable={editable}
                                onRequestSort={handleRequestSort}
                                rowCount={rowCount}
                                headCells={[headerColumn, ...otherColumns]}

                            />
                            <TableBody>
                                {stableSort(rows, getComparator(order, orderBy))
                                    .slice(rowsPerPage > 0 ? page * rowsPerPage : 0, rowsPerPage > 0 ? page * rowsPerPage + rowsPerPage : undefined)
                                    .map((row, index) => {
                                        const isItemSelected = isSelected(row[rowIdColumn.id]);
                                        const labelId = `enhanced-table-checkbox-${index}`;

                                        return (
                                            <TableRow
                                                hover
                                                onClick={(event) => handleClick(event, row[rowIdColumn.id])}
                                                onDoubleClick={(event) => onEdit(row[rowIdColumn.id])}
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                key={row[rowIdColumn.id]}
                                                selected={isItemSelected}
                                            >
                                                {editable === true ? (<TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isItemSelected}
                                                        inputProps={{ 'aria-labelledby': labelId }}
                                                    />
                                                </TableCell>) : (null)}
                                                <TableCell component="th" id={labelId} scope="row" padding="none">
                                                    {row[headerColumn.id]}
                                                </TableCell>
                                                {otherColumns.map(col => (
                                                    <TableCell
                                                        key={col.id}
                                                        align={col.numeric ? "right" : "left"}
                                                        sx={getPriorityCellSx(col.priority)}
                                                    >
                                                        {row[col.id]}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    })}
                                {emptyRows > 0 && (
                                    <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                                        <TableCell colSpan={6} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, { value: -1, label: 'All' }]}
                        component="div"
                        count={rowCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Container>
            </Paper>

        </div>
    );
}
